import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

export const CartContext = createContext();

const parseJsonSafe = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const getCurrentUserInfo = () => parseJsonSafe(localStorage.getItem('userInfo'), null);

const mergeCarts = (localCart, dbCart) => {
  // Backend cart is the source of truth; only add local items that don't exist in backend
  const merged = [...dbCart];
  localCart.forEach((item) => {
    const sameItem = merged.find((x) => x.id === item.id && x.size === item.size);
    if (!sameItem) {
      // Only add if it doesn't already exist in backend
      merged.push(item);
    }
    // If it exists in both, keep the backend version (don't add quantities)
  });
  return merged;
};

export const CartProvider = ({ children }) => {
  // --- 1. STATE INITIALIZATION ---
  
  const [cartItems, setCartItems] = useState(
    parseJsonSafe(localStorage.getItem('cartItems'), [])
  );

  const [shippingAddress, setShippingAddress] = useState(
    parseJsonSafe(localStorage.getItem('shippingAddress'), {})
  );

  const [paymentMethod, setPaymentMethod] = useState(
    parseJsonSafe(localStorage.getItem('paymentMethod'), 'UPI')
  );

  const [checkoutItems, setCheckoutItems] = useState(
    parseJsonSafe(localStorage.getItem('checkoutItems'), [])
  );

  const [checkoutSource, setCheckoutSource] = useState(
    parseJsonSafe(localStorage.getItem('checkoutSource'), 'cart')
  );

  const [isCartSynced, setIsCartSynced] = useState(false);

  const updateUserCartInDb = async (items) => {
    const currentUser = getCurrentUserInfo();
    if (!currentUser?.token) return;

    try {
      await axios.put(
        '/api/auth/cart',
        { cartItems: items },
        {
          headers: {
            Authorization: `Bearer ${currentUser.token}`,
          },
        }
      );
    } catch (err) {
      console.error('Failed to sync cart to backend:', err?.response?.data || err.message || err);
    }
  };

  const loadCartFromDb = async () => {
    const currentUser = getCurrentUserInfo();
    if (!currentUser?.token) {
      setIsCartSynced(true);
      return;
    }

    try {
      const localCart = parseJsonSafe(localStorage.getItem('cartItems'), []);
      const response = await axios.get('/api/auth/cart', {
        headers: {
          Authorization: `Bearer ${currentUser.token}`,
        },
      });

      const backendCart = Array.isArray(response.data) ? response.data : [];
      const mergedCart = mergeCarts(localCart, backendCart);
      setCartItems(mergedCart);
    } catch (err) {
      console.error('Failed to load cart from backend:', err?.response?.data || err.message || err);
    } finally {
      setIsCartSynced(true);
    }
  };

  // Sync Cart Items to LocalStorage automatically
  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    if (isCartSynced) {
      updateUserCartInDb(cartItems);
    }
  }, [cartItems, isCartSynced]);

  useEffect(() => {
    loadCartFromDb();
  }, []);

  useEffect(() => {
    localStorage.setItem('checkoutItems', JSON.stringify(checkoutItems));
  }, [checkoutItems]);

  useEffect(() => {
    localStorage.setItem('checkoutSource', JSON.stringify(checkoutSource));
  }, [checkoutSource]);


  // --- 2. CART FUNCTIONS ---

  const updateQty = (id, qty) => {
    setCartItems(
      cartItems.map((item) =>
        item.id === id ? { ...item, qty: Number(qty) } : item
      )
    );
  };

  const addToCart = (product, size) => {
    const existItem = cartItems.find((x) => x.id === product.id && x.size === size);
    if (existItem) {
      setCartItems(
        cartItems.map((x) => (x.id === existItem.id && x.size === size ? { ...product, qty: x.qty + 1, size } : x))
      );
    } else {
      setCartItems([...cartItems, { ...product, qty: 1, size }]);
    }
  };

  const removeFromCart = (id) => {
    setCartItems(cartItems.filter((x) => x.id !== id));
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('cartItems');
  };

  const startCartCheckout = () => {
    setCheckoutItems(cartItems);
    setCheckoutSource('cart');
    localStorage.setItem('checkoutItems', JSON.stringify(cartItems));
    localStorage.setItem('checkoutSource', JSON.stringify('cart'));
  };

  const startBuyNowCheckout = (product, size) => {
    const buyNowItem = { ...product, qty: 1, size };
    setCheckoutItems([buyNowItem]);
    setCheckoutSource('buyNow');
    localStorage.setItem('checkoutItems', JSON.stringify([buyNowItem]));
    localStorage.setItem('checkoutSource', JSON.stringify('buyNow'));
  };

  const clearCheckout = () => {
    setCheckoutItems([]);
    setCheckoutSource('cart');
    localStorage.removeItem('checkoutItems');
    localStorage.removeItem('checkoutSource');
  };


  // --- 3. CHECKOUT FUNCTIONS ---

  const saveShippingAddress = (data) => {
    setShippingAddress(data);
    localStorage.setItem('shippingAddress', JSON.stringify(data));
  };

  const savePaymentMethod = (data) => {
    setPaymentMethod(data);
    localStorage.setItem('paymentMethod', JSON.stringify(data));
  };

  // --- 4. PROVIDER EXPORT ---
  return (
    <CartContext.Provider 
      value={{ 
        cartItems, 
        addToCart, 
        removeFromCart, 
        updateQty,
        clearCart,
        checkoutItems,
        checkoutSource,
        startCartCheckout,
        startBuyNowCheckout,
        clearCheckout,
        shippingAddress, 
        saveShippingAddress, 
        paymentMethod,
        savePaymentMethod
      }}
    > 
      {children}
    </CartContext.Provider>
  );
};
